import { Link } from "react-router-dom";
import {
  ChevronRight, Check, Calendar, ArrowRight,
  GraduationCap, Crown, Flower2, HeartHandshake,
  Stethoscope, UserRound, Sparkles,
} from "lucide-react";
import { FadeIn } from "@/components/FadeIn";
import {
  MEMBERSHIP_PLANS, MONTHLY_WELLNESS_PLANS, MEMBERSHIP_PILLARS, MEMBERSHIP_KEYWORDS,
} from "@/lib/constants";
import { useEnquiry } from "@/lib/enquiry-context";

const PLAN_ICONS = { GraduationCap, Crown, Flower2, HeartHandshake };
const PILLAR_ICONS = { Flower2, Stethoscope, UserRound, Sparkles };

export default function MembershipsPlans() {
  const { openBook } = useEnquiry();

  return (
    <main data-testid="memberships-plans-page" className="bg-[#050505] text-[#f7f2e8]">
      {/* Hero */}
      <section className="relative overflow-hidden pt-[72px] md:pt-20">
        <img
          src="/assets/site/hero-bokeh2.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/80 to-[#050505]" />
        <div className="absolute inset-0 grain pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-[1480px] mx-auto px-5 md:px-10 pt-14 md:pt-20 pb-14">
          <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.24em] uppercase text-[#c9a14a]">
            <Link to="/" className="text-[#f7f2e8]/60 hover:text-[#c9a14a] transition-colors">Home</Link>
            <ChevronRight size={11} className="text-[#f7f2e8]/40" />
            <span>Membership Plans</span>
          </div>
          <FadeIn>
            <p className="mt-6 overline text-[#c9a14a]">Memberships</p>
            <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.06] max-w-3xl">
              <span className="text-[#f7f2e8]">Choose the Carlton </span>
              <span className="text-[#c9a14a]">membership that fits your life.</span>
            </h1>
            <p className="mt-5 text-[#f7f2e8]/75 text-base md:text-lg leading-relaxed max-w-2xl">
              Curated for prevention, recovery, slimming, anti-ageing, mental wellbeing and
              lifestyle health — with wallet credits, priority booking and exclusive rituals.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Long-term plans */}
      <section data-testid="membership-long-plans" className="relative">
        <div className="absolute inset-0 grain pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-[1480px] mx-auto px-5 md:px-10 pb-16 md:pb-20 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {MEMBERSHIP_PLANS.map((plan, i) => {
            const Icon = PLAN_ICONS[plan.icon] || Flower2;
            return (
              <FadeIn
                key={plan.slug}
                delay={i * 0.08}
                className={`relative flex flex-col rounded-2xl border ${
                  plan.featured
                    ? "border-[#c9a14a]/70 bg-gradient-to-b from-[#1a1408] to-[#0b0b0b] shadow-[0_20px_60px_-20px_rgba(201,161,74,0.35)]"
                    : "border-[#c9a14a]/25 bg-[#0b0b0b]"
                } p-7 md:p-8`}
                data-testid={`membership-plan-${plan.slug}`}
              >
                {plan.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#c9a14a] text-[#050505] text-[10px] font-bold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full">
                    Most Popular
                  </span>
                )}
                <div className="text-center">
                  <h3 className="font-serif-display text-2xl md:text-[26px] text-[#f7f2e8] leading-tight min-h-[64px]">
                    {plan.name}
                  </h3>
                  <span className="mt-4 inline-flex w-14 h-14 rounded-full border border-[#c9a14a]/50 items-center justify-center text-[#c9a14a]">
                    <Icon size={22} strokeWidth={1.3} />
                  </span>
                </div>
                <ul className="mt-7 space-y-3.5 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex gap-2.5 text-[13px] text-[#f7f2e8]/85 leading-relaxed">
                      <span className="mt-0.5 w-4 h-4 rounded-full bg-[#c9a14a]/25 flex items-center justify-center shrink-0">
                        <Check size={10} strokeWidth={3} className="text-[#c9a14a]" />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-7 pt-6 border-t border-[#c9a14a]/20 text-center">
                  <p className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.18em] uppercase text-[#c9a14a]/90">
                    <Calendar size={13} strokeWidth={1.5} />
                    {plan.duration}
                    {plan.scheduleNote && <span className="text-[#f7f2e8]/60 font-medium normal-case tracking-normal">· {plan.scheduleNote}</span>}
                  </p>
                  <p className="mt-4 font-numeric text-3xl md:text-4xl text-[#f7f2e8]">
                    {plan.price}
                    {plan.strikePrice && plan.strikePrice !== plan.price && (
                      <span className="ml-2 font-numeric text-lg text-red-400/70 line-through">{plan.strikePrice}</span>
                    )}
                  </p>
                  {plan.priceNote && (
                    <p className="mt-1 text-[11px] text-[#f7f2e8]/50">{plan.priceNote}</p>
                  )}
                  <button
                    onClick={() => openBook("membership")}
                    data-testid={`membership-plan-cta-${plan.slug}`}
                    className="btn-outline-gold mt-5 !py-2.5 !px-5 !text-[11px] w-full justify-center"
                  >
                    Enquire About This Plan
                  </button>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </section>

      {/* Monthly wellness pass */}
      <section data-testid="membership-monthly-plans" className="relative bg-[#0b0b0b] border-y border-[#c9a14a]/20">
        <div className="max-w-[1480px] mx-auto px-5 md:px-10 py-16 md:py-20">
          <FadeIn className="text-center">
            <p className="overline text-[#c9a14a]">Monthly Wellness Pass</p>
            <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-[#f7f2e8]">
              Flexible, month-to-month <span className="text-[#c9a14a]">wellness rituals.</span>
            </h2>
          </FadeIn>
          <div className="mt-12 grid gap-6 md:grid-cols-2 max-w-3xl mx-auto">
            {MONTHLY_WELLNESS_PLANS.map((m, i) => (
              <FadeIn
                key={m.tier}
                delay={i * 0.1}
                className={`relative rounded-2xl border p-7 md:p-8 flex items-center gap-6 ${
                  m.highlight
                    ? "border-[#c9a14a]/70 bg-gradient-to-br from-[#1a1408] to-[#0b0b0b]"
                    : "border-[#c9a14a]/25 bg-[#050505]"
                }`}
                data-testid={`monthly-plan-${m.tier.toLowerCase()}`}
              >
                <div className="flex-1">
                  <p className="flex items-baseline gap-2">
                    <span className="font-numeric text-lg text-[#c9a14a]/70">{m.number}</span>
                    <span className={`font-serif-display text-2xl ${m.highlight ? "text-[#c9a14a]" : "text-[#f7f2e8]"}`}>
                      {m.tier}
                    </span>
                  </p>
                  <p className="mt-3 text-[13px] text-[#f7f2e8]/80 leading-relaxed">{m.desc}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#c9a14a]">{m.cadence}</p>
                  <p className="mt-1 font-numeric text-3xl text-[#f7f2e8]">{m.price}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section data-testid="membership-pillars" className="bg-[#050505]">
        <div className="max-w-[1480px] mx-auto px-5 md:px-10 py-16 md:py-20">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-8">
            {MEMBERSHIP_PILLARS.map((p, i) => {
              const Icon = PILLAR_ICONS[p.icon] || Sparkles;
              return (
                <FadeIn key={p.title} delay={i * 0.08} className="flex items-start gap-4">
                  <span className="w-12 h-12 rounded-full border border-[#c9a14a]/50 flex items-center justify-center text-[#c9a14a] shrink-0">
                    <Icon size={20} strokeWidth={1.3} />
                  </span>
                  <div>
                    <p className="font-semibold text-[#f7f2e8] leading-snug">{p.title}</p>
                    <p className="mt-1 text-[12px] text-[#f7f2e8]/60 leading-snug">{p.sub}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Keyword strip */}
      <section aria-hidden="true" className="bg-[#0b0b0b] border-y border-[#c9a14a]/15">
        <div className="max-w-[1480px] mx-auto px-5 md:px-10 py-6 flex flex-wrap items-center justify-center gap-x-2 gap-y-2 text-[11px] md:text-[12px] font-bold tracking-[0.28em] uppercase text-[#c9a14a]/85">
          {MEMBERSHIP_KEYWORDS.map((k, i) => (
            <span key={k} className="inline-flex items-center gap-2">
              {i > 0 && <span className="text-[#c9a14a]/50">·</span>}
              <span>{k}</span>
            </span>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#050505]">
        <div className="max-w-[1480px] mx-auto px-5 md:px-10 py-16 md:py-20 text-center">
          <FadeIn>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#f7f2e8]">
              Ready to begin? <span className="text-[#c9a14a]">Book your assessment.</span>
            </h2>
            <p className="mt-4 text-[#f7f2e8]/70 text-base leading-relaxed max-w-xl mx-auto">
              A Carlton specialist will help you choose the membership that fits your goals.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <button
                onClick={() => openBook("assessment")}
                data-testid="memberships-book-assessment-button"
                className="btn-gold"
              >
                Book Your Assessment <ArrowRight size={15} strokeWidth={2} />
              </button>
              <Link to="/contact" data-testid="memberships-contact-link" className="btn-outline-gold !text-[#f7f2e8] hover:!text-[#050505]">
                Talk to Us
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </main>
  );
}
